import React from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import { TrendingUp, Users, Heart, DollarSign, Clock, Award, BarChart3 } from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const COLORS = ["#6366F1", "#10B981", "#F59E0B", "#64748B"];

export default function ExecutiveDashboard() {
  const { data: metrics, isLoading } = useQuery({
    queryKey: ["impact-metrics"],
    queryFn: async () => {
      const res = await apiClient.get("/impact/metrics");
      return res.data;
    },
  });

  if (isLoading) {
    return <div className="p-8 text-center text-sm text-slate-500">Loading Executive Impact Dashboard...</div>;
  }

  const {
    total_donations = 0,
    total_expenses = 0,
    net_program_funding = 0,
    people_served = 0,
    cost_per_person_served = 0,
    volunteer_hours_logged = 0,
    monthly_trend = [],
    program_breakdown = [],
  } = metrics || {};

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 font-sans">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-indigo-600" />
            Executive & Board Impact Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Real-time analytics connecting financial donor inputs directly to community outcomes & ROI.
          </p>
        </div>
        <div className="px-3.5 py-1.5 bg-indigo-50 border border-indigo-200 text-indigo-800 rounded-xl text-xs font-semibold">
          Project Miracle (Live Organization Scope)
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-2">
          <span className="text-xs font-semibold text-slate-500 flex items-center gap-1.5">
            <DollarSign className="w-4 h-4 text-emerald-600" /> Total Donated Inputs
          </span>
          <p className="text-2xl font-bold text-slate-900 font-mono">${total_donations.toLocaleString()}</p>
          <span className="text-[11px] text-emerald-600 font-medium">+18% vs last quarter</span>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-2">
          <span className="text-xs font-semibold text-slate-500 flex items-center gap-1.5">
            <Users className="w-4 h-4 text-indigo-600" /> People Served (Beneficiaries)
          </span>
          <p className="text-2xl font-bold text-slate-900 font-mono">{people_served.toLocaleString()}</p>
          <span className="text-[11px] text-indigo-600 font-medium">Direct community outreach</span>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-2">
          <span className="text-xs font-semibold text-slate-500 flex items-center gap-1.5">
            <Award className="w-4 h-4 text-amber-500" /> Cost Per Person Served
          </span>
          <p className="text-2xl font-bold text-slate-900 font-mono">${cost_per_person_served.toFixed(2)}</p>
          <span className="text-[11px] text-amber-600 font-medium">High program efficiency</span>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-2">
          <span className="text-xs font-semibold text-slate-500 flex items-center gap-1.5">
            <Clock className="w-4 h-4 text-purple-600" /> Volunteer Hours Contributed
          </span>
          <p className="text-2xl font-bold text-slate-900 font-mono">{volunteer_hours_logged.toLocaleString()} hrs</p>
          <span className="text-[11px] text-purple-600 font-medium">Equiv. $38,400 in labor</span>
        </div>
      </div>

      {/* Visual Analytics Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Monthly Financial vs Beneficiary Trend */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6 space-y-4 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-900">Donations vs. Program Expenses Trend</h2>
            <span className="text-xs text-slate-400 font-mono">2026 YTD</span>
          </div>

          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthly_trend}>
                <defs>
                  <linearGradient id="colorDonations" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366F1" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#6366F1" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                <XAxis dataKey="month" tickLine={false} axisLine={false} className="text-xs" />
                <YAxis tickLine={false} axisLine={false} className="text-xs" />
                <Tooltip />
                <Area type="monotone" dataKey="donations" stroke="#6366F1" fillOpacity={1} fill="url(#colorDonations)" name="Donations ($)" />
                <Area type="monotone" dataKey="expenses" stroke="#10B981" fillOpacity={1} fill="url(#colorExpenses)" name="Expenses ($)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Program Expense Breakdown */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4 shadow-sm flex flex-col justify-between">
          <h2 className="text-sm font-bold text-slate-900">Program Allocation Breakdown</h2>

          <div className="h-56 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={program_breakdown} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label>
                  {program_breakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="space-y-1.5 pt-2 border-t border-slate-100 text-xs">
            {program_breakdown.map((p, idx) => (
              <div key={p.name} className="flex justify-between items-center">
                <span className="flex items-center gap-1.5 text-slate-600">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                  {p.name}
                </span>
                <span className="font-bold text-slate-900">{p.value}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
